import type {
  AccessState,
  AdminSessionRow,
  AdminUserRow,
  ChangeSubscriptionInput,
  ContentOption,
  RegistrationFormSettings,
  RevokeEntitlementInput,
  SetGameOwnershipInput,
  SetManualAccessInput,
  SetTrialExemptionInput,
} from '@creativitas/shared';
import { apiFetch } from '@/lib/api';

export const ADMIN_USERS_KEY = ['admin', 'users'] as const;
export const ADMIN_CONTENT_KEY = ['admin', 'content'] as const;
export const ADMIN_SESSIONS_KEY = ['admin', 'sessions'] as const;

export const fetchAdminUsers = (): Promise<{ items: AdminUserRow[] }> =>
  apiFetch<{ items: AdminUserRow[] }>('/admin/users');

export const fetchAdminContent = (): Promise<{ items: ContentOption[] }> =>
  apiFetch<{ items: ContentOption[] }>('/admin/content');

export const fetchAdminSessions = (): Promise<{ items: AdminSessionRow[] }> =>
  apiFetch<{ items: AdminSessionRow[] }>('/admin/sessions');

type AccessResponse = { access: AccessState };

/** Подтвердить почту без письма — когда почтовик получателя его не пропускает (§14). */
export const confirmEmail = (userId: string): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>(`/admin/users/${userId}/confirm-email`, { method: 'POST' });

/** Тариф вручную: выдать или продлить, установить до даты, снять (docs/PAYMENTS.md, §10). */
export const changeSubscription = (
  userId: string,
  input: ChangeSubscriptionInput,
): Promise<AccessResponse> =>
  apiFetch<AccessResponse>(`/admin/users/${userId}/subscription`, { method: 'POST', body: input });

/** Отзыв одной выдачи с причиной: остаётся в истории, срок пересчитывается. */
export const revokeEntitlement = (
  userId: string,
  entitlementId: string,
  input: RevokeEntitlementInput,
): Promise<AccessResponse> =>
  apiFetch<AccessResponse>(`/admin/users/${userId}/entitlements/${entitlementId}/revoke`, {
    method: 'POST',
    body: input,
  });

/** Применяется ли к человеку пробный период (§11). */
export const setTrialExemption = (
  userId: string,
  input: SetTrialExemptionInput,
): Promise<AccessResponse> =>
  apiFetch<AccessResponse>(`/admin/users/${userId}/trial`, { method: 'POST', body: input });

export const setGameOwnership = (
  userId: string,
  input: SetGameOwnershipInput,
): Promise<AccessResponse> =>
  apiFetch<AccessResponse>(`/admin/users/${userId}/game-ownership`, {
    method: 'POST',
    body: input,
  });

export const setManualAccess = (
  userId: string,
  input: SetManualAccessInput,
): Promise<AccessResponse> =>
  apiFetch<AccessResponse>(`/admin/users/${userId}/manual-access`, { method: 'POST', body: input });

/** Надписи и согласия экрана регистрации: их правит владелец платформы (§14). */
export const fetchRegistrationForm = (): Promise<RegistrationFormSettings> =>
  apiFetch<RegistrationFormSettings>('/admin/settings/registration');

export const saveRegistrationForm = (
  input: RegistrationFormSettings,
): Promise<RegistrationFormSettings> =>
  apiFetch<RegistrationFormSettings>('/admin/settings/registration', { method: 'PUT', body: input });
